import { ClockIcon } from '@components/Icons/ClockIcon';
import { TriangleExclamationMarkIcon } from '@components/Icons/TriangleExclamationMarkIcon';
import { SelectionCheckbox } from '@components/SelectionCheckbox/SelectionCheckbox';
import { TopOverlay } from '@components/TopOverlay/TopOverlay';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import Replay from '@mui/icons-material/Replay';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { getStatusLabel, getStatusTooltip } from '@pages/Library/utils/mediaStatus';
import { BaseSelection } from '@stores/MomentSelection/BaseSelection';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import { MediaFolderLocation } from '../../Folders/MediaFolderLocation';
import css from '../MediaCard.module.scss';
import { MediaCardInfo } from './MediaCardInfo';
import { ProgressBar } from './ProgressBar';
import { StatusPill } from './StatusPill';
import { StatusText } from './StatusText';

type CloseButtonProps = {
  onClose: () => void;
  tooltip: string;
};

type RetryButtonProps = {
  onRetry: () => void;
  tooltip: string;
};

type SelectionCheckboxProps = {
  selection: BaseSelection<any>;
  itemId: string;
};

type StatusOverlayProps = {
  type: 'queued-upload' | 'queued-processing' | 'errored' | 'processing' | 'uploading';
  subtype?: 'processing-with-video' | 'processing-without-video';
  progress?: number;
  timeLeft?: string;
  subtext?: string;
  closeButtonProps?: CloseButtonProps;
  retryButtonProps?: RetryButtonProps;
  selectionCheckboxProps?: SelectionCheckboxProps;
  title?: string;
  media?: Media;
  duration?: number;
  isCompact?: boolean;
};

export const StatusOverlay = ({
  type,
  subtype,
  progress,
  timeLeft,
  subtext,
  closeButtonProps,
  retryButtonProps,
  selectionCheckboxProps,
  title,
  media,
  duration,
  isCompact,
}: StatusOverlayProps) => {
  const getStatusPill = () => {
    switch (type) {
      case 'queued-upload':
        return (
          <StatusPill tooltip={getStatusTooltip('queued-upload', title)} status="queued">
            <StatusText icon={<ClockIcon />} text={getStatusLabel('queued-upload')} />
            <ProgressBar />
          </StatusPill>
        );
      case 'queued-processing':
        return (
          <StatusPill tooltip={getStatusTooltip('queued', title)} status="processing">
            <StatusText icon={<InfoIcon />} text={getStatusLabel('queued', progress)} />
            <ProgressBar progress={progress ?? 0} />
          </StatusPill>
        );
      case 'errored':
        return (
          <StatusPill
            className={css.centerPillErrored}
            tooltip="This file is errored and needs to be re-uploaded. Please contact support if the problem persists."
            status="errored"
          >
            <StatusText icon={<TriangleExclamationMarkIcon />} text="Errored" />
          </StatusPill>
        );
      case 'processing':
        const displayStatus = subtype || 'processing-without-video';
        const tooltip = getStatusTooltip(displayStatus, title);
        return (
          <StatusPill className={css.centerPill} tooltip={tooltip} status="processing">
            <StatusText icon={<InfoIcon />} text={getStatusLabel(displayStatus, progress)} />
            <ProgressBar progress={progress ? progress : 0} />
          </StatusPill>
        );
      case 'uploading':
        return (
          <StatusPill status="uploading">
            <div>
              <StatusText icon={<ClockIcon />} text={`Uploading (${progress}%${timeLeft})`} />
              {subtext ? <div className={css.subtext}>{subtext}</div> : null}
            </div>
            <ProgressBar progress={progress ? progress : 0} />
          </StatusPill>
        );
    }
  };

  const retryButton = retryButtonProps ? (
    <div className={css.iconButton} onClick={(e) => e.stopPropagation()}>
      <Tooltip title={retryButtonProps.tooltip} placement="top" arrow>
        <IconButton size="small" onClick={retryButtonProps.onRetry}>
          <Replay />
        </IconButton>
      </Tooltip>
    </div>
  ) : null;

  const selectionCheckbox = selectionCheckboxProps ? (
    <div className={css.iconButton} onClick={(e) => e.stopPropagation()}>
      <SelectionCheckbox
        selection={selectionCheckboxProps.selection}
        itemId={selectionCheckboxProps.itemId}
      />
    </div>
  ) : null;

  const closeButton = closeButtonProps ? (
    <div className={css.iconButton} onClick={(e) => e.stopPropagation()}>
      <Tooltip title={closeButtonProps.tooltip} placement="top" arrow>
        <IconButton size="small" onClick={closeButtonProps.onClose}>
          <CloseIcon />
        </IconButton>
      </Tooltip>
    </div>
  ) : null;

  // Determine title props.
  // For processing-with-video, VideoCard's own lower bar is hidden (showLowerBar: false)
  // so StatusOverlay owns the title display — this is intentional, not a double render.
  const showTitleWithMedia =
    (type === 'uploading' ||
      type === 'processing' ||
      type === 'queued-upload' ||
      type === 'queued-processing' ||
      type === 'errored') &&
    media;
  const showTitleWithoutMedia =
    (type === 'uploading' || type === 'queued-upload' || type === 'queued-processing') &&
    title &&
    !media;
  const titleProps = {
    value: showTitleWithMedia
      ? (media.metadata?.name ?? title ?? '-')
      : showTitleWithoutMedia
        ? title
        : '',
    origin: showTitleWithMedia ? media.origin : undefined,
    show: Boolean(showTitleWithMedia || showTitleWithoutMedia),
  };

  return (
    <div className={classNames(css.statusOverlay, css.noPointerEvents)}>
      <MediaCardInfo
        title={titleProps}
        duration={{ value: duration, show: duration != null }}
        commentCount={media?.commentCount}
        isCompact={isCompact}
        location={showTitleWithMedia && <MediaFolderLocation media={media} />}
      >
        {getStatusPill()}
        {(retryButton || selectionCheckbox || closeButton) && (
          <TopOverlay
            rightChildren={
              <>
                {retryButton}
                {selectionCheckbox}
                {closeButton}
              </>
            }
          />
        )}
      </MediaCardInfo>
    </div>
  );
};
